<template>
  <div
    class="flex flex-col py-4 text-base md:flex-row md:gap-10"
    :class="{
      'opacity-50': !isInStock(variant),
    }"
    :data-testid="`product-cart-${product.id}`"
  >
    <div class="max-w-[290px] self-center md:w-1/4 md:max-w-none md:self-auto">
      <NuxtLink :to="productPath" @click="trackSelectItem(product)">
        <NuxtPicture
          v-if="imageHash"
          :src="imageHash"
          :alt="productConfig.name"
          provider="fim"
          class="object-cover"
          sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw"
        />
        <ProductPromotionBadges :product="product" view="basket" class="mt-2" />
        <ProductPromotionFreeGiftBadge
          v-if="isFreeGift"
          :style="promotionStyle"
          class="absolute bottom-2 left-2"
        />
      </NuxtLink>
    </div>
    <div class="min-w-[210px] md:w-3/4">
      <ProductConfigurationList
        data-testid="rxConfigurationList"
        class="pb-4 sm:pb-4"
        :product="productConfig"
        :product-configuration="productConfigItems"
        :product-path="productPath"
        :description="productColorAndSize"
        :quantity="quantity"
        :is-quantity-change-disabled="hasQuantityChangeDisabled"
        :available-quantity="availableQuantity"
        @click:change-quantity="
          (quantity: number) =>
            $emit('click:change-quantity', {
              quantity,
              product,
              variant,
            } as AddBasketData)
        "
        @click:remove="$emit('click:delete')"
      />
      <div class="flex flex-col gap-4">
        <BasketSubscriptionOptions
          v-if="
            isSubscriptionEnabled &&
            isProductSubscriptionEligible(basketItem.product)
          "
          class="relative"
          :basket-item="basketItem"
          :product="product"
          :variant="variant"
          :quantity="quantity"
          :subscription-discounts="subscriptionDiscounts"
          @subscription:change="
            (data: SubscriptionChangeData) => $emit('subscription:change', data)
          "
          @subscription:stop="
            (data: SubscriptionChangeData) => $emit('subscription:stop', data)
          "
        />
        <SightData
          v-if="supportsPrescriptionValues"
          :openable="true"
          class="relative transition-opacity"
          :title="$t('rxbasketcardDefault.sightData.title')"
          :details="prescriptionValues"
          data-testid="contact-lens-detail"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BasketItemDisplayDataKey } from '@scayle/storefront-api'
import {
  isInStock,
  type BaskteItemDisplayDataItem,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import type { FimBasketItemCustomData } from '~/composables/useFimBasket'

export type AddBasketData = {
  quantity: number
  product: Product
  variant: Variant
}

defineEmits([
  'click:delete',
  'click:change-quantity',
  'subscription:change',
  'subscription:stop',
])

const props = defineProps({
  basketItem: {
    type: Object as PropType<FimBasketItem>,
    required: true,
  },
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  imageHash: {
    type: String as PropType<string>,
    default: undefined,
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  availableQuantity: {
    type: Number as PropType<number>,
    default: undefined,
  },
  displayData: {
    type: Object as PropType<
      Partial<Record<BasketItemDisplayDataKey, BaskteItemDisplayDataItem>>
    >,
    default: undefined,
  },
  customData: {
    type: Object as PropType<FimBasketItemCustomData>,
    required: true,
  },
  subscriptionPricePromotionKey: {
    type: String as PropType<string>,
    default: () => 'subscription',
  },
})

const { $i18n, $fimLocalePath, $featureToggle } = useNuxtApp()
const { trackSelectItem } = await useTrackingEvents()
const { getProductDetailRoute } = useRouteHelpers()

const basketItem = toRef(props, 'basketItem')
const subscriptionPricePromotionKey = toRef(
  props,
  'subscriptionPricePromotionKey',
)

const basketItemProduct = computed(() => basketItem.value.product)

const subscription = await useSubscription(
  basketItemProduct,
  subscriptionPricePromotionKey,
  [props.variant],
  `product-subscription-${basketItemProduct.value.id}`,
)

const { subscriptionPrice, subscriptionDiscounts } = subscription
const { isSubscriptionEnabled } = $featureToggle

const hasSubscription = computed(
  () => isSubscriptionEnabled.value && hasSubscriptionDefined(basketItem),
)

const { isFreeGift, promotionStyle } = await useBasketItemPromotion(basketItem)

const productConfig = computed(() => {
  const mainItem = getProductConfigMainItem(
    props.product,
    { ...props.variant, price: basketItem.value.price.unit },
    props.displayData,
  )

  if (
    hasSubscription.value &&
    subscriptionPrice?.value?.withTax === basketItem?.value?.price?.unit?.withTax
  ) {
    mainItem.value = subscriptionPrice.value.withTax
    mainItem.price = subscriptionPrice.value
  }

  return mainItem
})

const productColorAndSize = getFrameColorAndSize(props.product, props.variant)

const productCategory = props.product && getProductCategory(props.product)

const lensesDetails = computed(() => {
  if (productCategory === ProductCategories.contactLenses) {
    return getLensDetailsFromBasketItem(props.customData, props.variant)
  }
  return undefined
})

const productConfigItems = computed(() => {
  if (productCategory && productCategory !== ProductCategories.glasses) {
    return getProductConfigItems(
      props.product,
      props.variant,
      productCategory,
      $i18n,
      props.displayData,
    )
  }
  return []
})

const productPath = computed(() =>
  $fimLocalePath(
    getProductDetailRoute(props.product, {
      variantid: props.variant.id,
      ...lensesDetails.value,
    }),
  ),
)

const supportsPrescriptionValues = computed(() =>
  Boolean(props.customData?.prescriptionValues),
)

const prescriptionValues = computed(() => props.customData.prescriptionValues)

const hasQuantityChangeDisabled = computed(() => {
  if (isFrameOrder(basketItem.value)) {
    return false
  }
  return isQuantityChangeDisabled(props.product)
})
</script>
